import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Icon,
  Collapse,
  Divider,
  Select,
  Button,
  Badge,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  SimpleGrid,
  Tooltip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from "@chakra-ui/react";
import { FiClock, FiSend } from "react-icons/fi";
import { FaLock, FaUnlock } from "react-icons/fa";
import { SlackChannel, SlackNotificationConfig, SlackNotificationRequest, SlackUser } from "build-ts/slack_notification";
import { getSlackChannels, getSlackChannelUsers } from "services/AnalystService";
import { Header } from "build-ts/metadata";
import { ScheduledScenarioJobProto, ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { getScheduledJob } from "services/ScheduleServices";
import CronBuilder from "./CronBuilder";
import lodash from "lodash";
import { MdCode, MdPlayArrow } from "react-icons/md";
import { LuInfo } from "react-icons/lu";
import { useUser } from "context/UserContext";
import { BiTargetLock } from "react-icons/bi";
import { PiPresentationFill } from "react-icons/pi";

interface JobCreationComponentProps {
  jobName: string;
  setJobName: (value: string) => void;
  selectedJob?: Header;
  isCreatingNew?: boolean;
  triggerSave?: boolean;
  setTriggerSave?: (value: boolean) => void;
  setIsSaving?: (value: boolean) => void;
  onSaveSuccess?: () => void;
  setHasFormChanges?: (hasChanges: boolean) => void;
  onRun?: () => void;
}

const JobCreationComponent: React.FC<JobCreationComponentProps> = ({
  jobName,
  setJobName,
  selectedJob,
  isCreatingNew = false,
  triggerSave = false,
  setTriggerSave,
  setIsSaving,
  onSaveSuccess,
  setHasFormChanges,
  onRun,
}) => {
  const { id: analystId } = useParams();
  const { userId } = useUser();
  // Analysis state
  const [trigger, setTrigger] = useState<string>("");
  const [analysisInstructions, setAnalysisInstructions] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [historicalInstructions, setHistoricalInstructions] = useState<string>("");
  const [temporaryInstructions, setTemporaryInstructions] = useState<string>("");

  // Communication state
  const [slackTitle, setSlackTitle] = useState<string>("");
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(null);
  const [channelUsers, setChannelUsers] = useState<SlackUser[]>([]);
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<SlackUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [lastFetchedChannelId, setLastFetchedChannelId] = useState<string | null>(null);

  // Collapsible sections state
  const [isCommunicationExpanded, setIsCommunicationExpanded] = useState<boolean>(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  // Schedule state
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  // Internal Instructions Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notesLocked, setNotesLocked] = useState(true);

  // Original data state for tracking changes
  const [originalData, setOriginalData] = useState<{
    jobName: string;
    trigger: string;
    analysisInstructions: string;
    reportingInstructions: string;
    historicalInstructions: string;
    temporaryInstructions: string;
    slackTitle: string;
    selectedSlackChannel: SlackChannel | null;
    selectedSlackUsers: SlackUser[];
    cronExpression: string;
  }>({
    jobName: "",
    trigger: "",
    analysisInstructions: "",
    reportingInstructions: "",
    historicalInstructions: "",
    temporaryInstructions: "",
    slackTitle: "",
    selectedSlackChannel: null,
    selectedSlackUsers: [],
    cronExpression: "",
  });

  // Get current form data
  const getCurrentFormData = () => {
    return {
      jobName,
      trigger,
      analysisInstructions,
      reportingInstructions,
      historicalInstructions,
      temporaryInstructions,
      slackTitle,
      selectedSlackChannel,
      selectedSlackUsers,
      cronExpression,
    };
  };

  // Check if form has changes
  useEffect(() => {
    const currentFormData = getCurrentFormData();
    const isChanged = !lodash.isEqual(originalData, currentFormData);
    setHasFormChanges?.(isChanged);
  }, [
    jobName,
    trigger,
    analysisInstructions,
    reportingInstructions,
    historicalInstructions,
    temporaryInstructions,
    slackTitle,
    selectedSlackChannel,
    selectedSlackUsers,
    cronExpression,
    isActive,
  ]);

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);

      const jobNameValue = response.header?.name || "";
      const triggerValue = response.scheduled_analysis?.trigger || "";
      const analysisInstructionsValue = response.scheduled_analysis?.analysis_instructions || "";
      const reportingInstructionsValue = response.scheduled_analysis?.reporting_instructions || "";
      const historicalInstructionsValue = response.scheduled_analysis?.historical_instructions || "";
      const temporaryInstructionsValue = response.scheduled_analysis?.temporary_instructions || "";
      const slackTitleValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.message_title || "";
      const slackChannelValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.channel || null;
      const slackUsersValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.tag_users || [];
      const cronExprValue = response.cron_expr || "";
      const isActiveValue = response.is_active || false;

      setJobName(jobNameValue);
      setTrigger(triggerValue);
      setAnalysisInstructions(analysisInstructionsValue);
      setReportingInstructions(reportingInstructionsValue);
      setHistoricalInstructions(historicalInstructionsValue);
      setTemporaryInstructions(temporaryInstructionsValue);
      setSlackTitle(slackTitleValue);
      setSelectedSlackChannel(slackChannelValue);
      setSelectedSlackUsers(slackUsersValue);
      setCronExpression(cronExprValue);
      setIsActive(isActiveValue);

      setOriginalData({
        jobName: jobNameValue,
        trigger: triggerValue,
        analysisInstructions: analysisInstructionsValue,
        reportingInstructions: reportingInstructionsValue,
        historicalInstructions: historicalInstructionsValue,
        temporaryInstructions: temporaryInstructionsValue,
        slackTitle: slackTitleValue,
        selectedSlackChannel: slackChannelValue,
        selectedSlackUsers: slackUsersValue,
        cronExpression: cronExprValue,
      });
    }
  };

  const handleSaveJob = async () => {
    if (!jobName.trim()) {
      showToast("Please enter a job name", "", "warning");
      return;
    }

    setIsSaving?.(true);

    const job = ScheduledJobProto.create({
      header: Header.create({
        id: selectedJob?.id || uuidv4(),
        name: jobName.trim(),
        type: MetadataTypeEnum.SCHEDULED_JOB,
        owner_id: analystId,
      }),
      type: ScheduledJobType.SCENARIO_JOB,
      cron_expr: cronExpression,
      is_active: isActive,
      scheduled_analysis: ScheduledScenarioJobProto.create({
        trigger: trigger.trim(),
        analysis_instructions: analysisInstructions.trim(),
        reporting_instructions: reportingInstructions.trim(),
        historical_instructions: historicalInstructions.trim(),
        temporary_instructions: temporaryInstructions.trim(),
        slack_notification_request: SlackNotificationRequest.create({
          notification_config: SlackNotificationConfig.create({
            channel: selectedSlackChannel
              ? SlackChannel.create({
                id: selectedSlackChannel.id,
                name: selectedSlackChannel.name,
                is_private: selectedSlackChannel.is_private,
                num_members: selectedSlackChannel.num_members,
              })
              : undefined,
            tag_users: selectedSlackUsers,
            message_title: slackTitle.trim(),
          }),
        }),
      }),
    });

    const response = await putScheduledJob(job);
    setIsSaving?.(false);

    if (response) {
      showToast(
        isCreatingNew ? "Job created successfully" : "Job updated successfully",
        "",
        "success"
      );
      onSaveSuccess?.();
    } else {
      showToast(
        isCreatingNew ? "Failed to create job" : "Failed to update job",
        "",
        "error"
      );
    }
  };

  // Watch for triggerSave changes to trigger save
  useEffect(() => {
    if (triggerSave) {
      handleSaveJob();
      setTriggerSave?.(false);
    }
  }, [triggerSave]);

  // Fetch slack channels on component mount
  useEffect(() => {
    const fetchSlackChannels = async () => {
      const channels = await getSlackChannels();
      if (channels) {
        setSlackChannels(channels);
      }
    };
    fetchSlackChannels();
  }, []);

  // Fetch job details when selectedJob changes
  useEffect(() => {
    if (selectedJob?.id) {
      fetchJobDetails(selectedJob.id);
    } else {
      setJobName("");
      setTrigger("");
      setAnalysisInstructions("");
      setReportingInstructions("");
      setHistoricalInstructions("");
      setTemporaryInstructions("");
      setSlackTitle("");
      setSelectedSlackChannel(null);
      setSelectedSlackUsers([]);
      setIsLoadingUsers(false);
      setLastFetchedChannelId(null);
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);

      setOriginalData({
        jobName: "",
        trigger: "",
        analysisInstructions: "",
        reportingInstructions: "",
        historicalInstructions: "",
        temporaryInstructions: "",
        slackTitle: "",
        selectedSlackChannel: null,
        selectedSlackUsers: [],
        cronExpression: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id]);

  // Fetch slack channel users when channel is selected
  useEffect(() => {
    const fetchChannelUsers = async () => {
      if (selectedSlackChannel?.id) {
        if (selectedSlackChannel.id === lastFetchedChannelId) {
          return;
        }

        setIsLoadingUsers(true);
        try {
          const users = await getSlackChannelUsers(selectedSlackChannel.id);
          if (users) {
            setChannelUsers(users);
          } else {
            setChannelUsers([]);
          }
          setLastFetchedChannelId(selectedSlackChannel.id);
        } catch (error) {
          console.error("Error fetching channel users:", error);
          setChannelUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      } else {
        setChannelUsers([]);
        setLastFetchedChannelId(null);
        setSelectedSlackUsers([]);
        setIsLoadingUsers(false);
      }
    };
    fetchChannelUsers();
  }, [selectedSlackChannel]);

  const toggleCommunicationExpansion = () => {
    setIsCommunicationExpanded(!isCommunicationExpanded);
  };

  // Handle Slack channel selection
  const handleSlackChannelChange = (channelId: string) => {
    if (channelId === "") {
      setSelectedSlackChannel(null);
    } else {
      const channel = slackChannels.find((c) => c.id === channelId);
      setSelectedSlackChannel(channel || null);
    }
    setSelectedSlackUsers([]);
  };

  // Remove selected slack user
  const removeSlackUser = (userId: string) => {
    setSelectedSlackUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  return (
    <VStack spacing={0} align="stretch" h="100%" overflowY="auto" px={2}>

      <VStack spacing={6} align="stretch" px={6} py={5}>
        {/* Name row — with inline Run + Internal Instructions */}
        <HStack spacing={3} align="center">
          <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0} whiteSpace="nowrap" flexShrink={0}>
            Name
          </Text>
          <Input
            size="md"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="e.g. Weekly CPL Monitor…"
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
            }}
            fontWeight="600"
            fontSize="sm"
            flex={1}
          />
          {isCreatingNew === false && (
            <Button
              size="sm"
              colorScheme="green"
              variant="solid"
              leftIcon={<Icon as={MdPlayArrow} boxSize={5} />}
              onClick={onRun}
              flexShrink={0}
              fontWeight="700"
              _hover={{ bg: "green.700" }}
              bg="green.600"
            >
              Run
            </Button>
          )}
          <Button
            size="sm"
            variant="solid"
            colorScheme="blue"
            bg="blue.600"
            color="white"
            _hover={{ bg: "blue.700" }}
            flexShrink={0}
            leftIcon={<Icon as={MdCode} boxSize={5} />}
            onClick={() => { setNotesLocked(true); setIsModalOpen(true); }}
            fontWeight="700"
          >
            Internal Instructions
          </Button>
        </HStack>

        {/* Analysis Objective + Reporting Instructions — side by side */}
        <SimpleGrid columns={2} spacing={4}>
          <FormControl display="flex" flexDirection="column">
            <HStack spacing={2} mb={2} align="center">
              <Icon as={BiTargetLock} boxSize={6} color="purple.700" />
              <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
                Analysis Objective
              </Text>
              <Tooltip
                label="When should this analysis fire? Define the metric thresholds and data conditions."
                placement="top"
                hasArrow
                fontSize="sm"
                bg="blue.50"
                color="gray.700"
                py={2}
                px={3}
                borderRadius="md"
                fontWeight="600"
              >
                <span>
                  <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                </span>
              </Tooltip>
            </HStack>
            <Textarea
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              placeholder="e.g. CPL_today > 100 AND CPL_3day_avg < 50, spend > 0…"
              rows={11}
              resize="none"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
              }}
              fontWeight="500"
              fontSize="sm"
            />
          </FormControl>

          <FormControl display="flex" flexDirection="column">
            <HStack spacing={2} mb={2} align="center">
              <Icon as={PiPresentationFill} boxSize={6} color="purple.700" />
              <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
                Reporting Instructions
              </Text>
              <Tooltip
                label="How should findings be presented? Specify columns, ranking, and narrative format."
                placement="top"
                hasArrow
                fontSize="sm"
                maxW="240px"
                bg="blue.50"
                color="gray.700"
                py={2}
                px={3}
                borderRadius="md"
                fontWeight="600"
              >
                <span>
                  <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                </span>
              </Tooltip>
            </HStack>
            <Textarea
              value={reportingInstructions}
              onChange={(e) => setReportingInstructions(e.target.value)}
              placeholder="e.g. Table of top 10 products by sales, include delta and price change %…"
              rows={11}
              resize="none"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
              }}
              fontWeight="500"
              fontSize="sm"
            />
          </FormControl>
        </SimpleGrid>
      </VStack>

      <Divider my={1} />

      {/* Schedule + Live/Paused status side by side */}
      <VStack spacing={5} align="stretch" px={6} py={5} h="100%" overflowY="auto">
        <HStack spacing={4} align="flex-start">
          {/* CronBuilder — takes up most of the space */}
          <Box flex={1}>
            <HStack spacing={2} mb={2}>
              <Icon as={FiClock} boxSize={5} color="purple.700" />
              <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
                Schedule
              </Text>
            </HStack>
            <Box border="1px" borderColor="gray.200" borderRadius="md">
              <CronBuilder
                cronExpression={cronExpression}
                setCronExpression={setCronExpression}
                isActive={isActive}
                setIsActive={setIsActive}
                jobDetails={selectedJobDetails}
              />
            </Box>
          </Box>
        </HStack>

        {/* Temporary Instructions */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="700" color="gray.700" mb={2}>
            Temporary Instructions
          </FormLabel>
          <Textarea
            size="md"
            value={temporaryInstructions}
            onChange={(e) => setTemporaryInstructions(e.target.value)}
            placeholder="One-off instructions that override defaults for a single run…"
            fontSize="sm"
            rows={4}
            resize="none"
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
            }}
          />
        </FormControl>

        {/* Communication Section */}
        {userId === "02c73738-9481-4d0f-85c4-e927c6a09d09" && (
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleCommunicationExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiSend} boxSize={4} color="purple.700" />
              <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
                Communication
              </Text>
            </HStack>

            <Collapse in={isCommunicationExpanded} animateOpacity>
              <VStack spacing={4} align="stretch" p={3}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">
                      Notification Title
                    </FormLabel>
                    <Input
                      size="sm"
                      borderRadius="md"
                      value={slackTitle}
                      onChange={(e) => setSlackTitle(e.target.value)}
                      placeholder="Enter notification title"
                      _focus={{
                        borderColor: "purple.700",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
                      }}
                      fontWeight="500"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">
                      Slack Channel
                    </FormLabel>
                    <Select
                      size="sm"
                      borderRadius="md"
                      value={selectedSlackChannel?.id || ""}
                      onChange={(e) => handleSlackChannelChange(e.target.value)}
                      placeholder="Choose a channel"
                    >
                      {slackChannels.map((channel) => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </Select>
                    {selectedSlackChannel && (
                      <Flex mt={2}>
                        <Badge colorScheme="blue" fontSize="xs">
                          {selectedSlackChannel.name}
                        </Badge>
                      </Flex>
                    )}
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">
                    Notify Users
                  </FormLabel>
                  <Popover placement="bottom-start" strategy="fixed">
                    <PopoverTrigger>
                      <Button
                        size="sm"
                        variant="outline"
                        isDisabled={!selectedSlackChannel || isLoadingUsers}
                        isLoading={isLoadingUsers}
                        loadingText="Loading users..."
                        bg={!selectedSlackChannel || isLoadingUsers ? "gray.100" : "white"}
                        textAlign="left"
                        fontWeight="normal"
                        width="100%"
                        justifyContent="flex-start"
                        _hover={!selectedSlackChannel || isLoadingUsers ? {} : { bg: "gray.50" }}
                      >
                        {isLoadingUsers
                          ? "Loading users..."
                          : selectedSlackUsers.length === 0
                            ? selectedSlackChannel
                              ? "Choose users"
                              : "Select a channel first"
                            : selectedSlackUsers.length === 1
                              ? selectedSlackUsers[0].display_name || selectedSlackUsers[0].name
                              : `${selectedSlackUsers.length} users selected`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent width="300px" zIndex="popover">
                      <PopoverBody maxHeight="200px" overflowY="auto" p={3}>
                        <VStack spacing={2} align="stretch">
                          {isLoadingUsers ? (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              Loading users...
                            </Text>
                          ) : channelUsers.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              No users available
                            </Text>
                          ) : (
                            channelUsers.map((user) => (
                              <HStack key={user.id} spacing={3} w="100%" align="center">
                                <Checkbox
                                  isChecked={selectedSlackUsers.some((u) => u.id === user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (!selectedSlackUsers.some((u) => u.id === user.id)) {
                                        setSelectedSlackUsers((prev) => [...prev, user]);
                                      }
                                    } else {
                                      setSelectedSlackUsers((prev) =>
                                        prev.filter((u) => u.id !== user.id)
                                      );
                                    }
                                  }}
                                  size="sm"
                                  colorScheme="purple"
                                />
                                <Text fontSize="sm" color="gray.700" ml={1} mt={1}>
                                  {user.display_name || user.name}
                                </Text>
                              </HStack>
                            ))
                          )}
                        </VStack>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                  {selectedSlackUsers.length > 0 && (
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {selectedSlackUsers.map((user) => (
                        <Badge
                          key={user.id}
                          colorScheme="purple"
                          fontSize="xs"
                          cursor="pointer"
                          onClick={() => removeSlackUser(user.id!)}
                          _hover={{ bg: "purple.200" }}
                          textTransform="none"
                        >
                          {user.display_name || user.name} ✕
                        </Badge>
                      ))}
                    </Flex>
                  )}
                </FormControl>
              </VStack>
            </Collapse>
          </FormControl>
        )}
      </VStack>

      {/* Internal Instructions Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent overflow="hidden">
          <Box
            px={5}
            py={3}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
          >
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.700" fontFamily="mono" mb={0} fontWeight="700">
                  Analysis Instructions
                </Text>
                <Text fontSize="xs" color="gray.500" mb={0} fontFamily="mono">
                  {notesLocked ? "(locked — click 🔒 to edit)" : "(editing…)"}
                </Text>
              </HStack>
              <Tooltip
                label={notesLocked ? "Unlock to edit" : "Lock editing"}
                placement="top"
                hasArrow
                fontSize="sm"
              >
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Icon as={notesLocked ? FaLock : FaUnlock} boxSize={3} />}
                  color={notesLocked ? "gray.500" : "purple.600"}
                  _hover={{ color: notesLocked ? "gray.700" : "purple.700", bg: "purple.50" }}
                  onClick={() => setNotesLocked(prev => !prev)}
                >
                  {notesLocked ? "Edit" : "Editing"}
                </Button>
              </Tooltip>
            </HStack>
          </Box>

          <ModalBody p={0}>
            <Textarea
              value={analysisInstructions}
              onChange={(e) => setAnalysisInstructions(e.target.value)}
              placeholder={"# analysis instructions\n# how to build the data query…"}
              rows={20}
              resize="none"
              isReadOnly={notesLocked}
              fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Source Code Pro', monospace"
              fontSize="sm"
              lineHeight="1.6"
              bg={notesLocked ? "gray.50" : "white"}
              color={notesLocked ? "gray.700" : "gray.800"}
              border="none"
              borderRadius="none"
              _focus={{ border: "none", boxShadow: "none" }}
              _readOnly={{ cursor: "default", opacity: 1, bg: "gray.50", color: "gray.700" }}
              p={5}
              sx={{
                caretColor: "green.300",
                "::placeholder": {
                  color: "gray.500",
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                },
              }}
            />
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100">
            <Button colorScheme="purple" size="sm" onClick={() => setIsModalOpen(false)}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default JobCreationComponent;
